// Main JavaScript file

import Tracking from './tracking.js';
import TrackingCore from './tracking-core.js';
import { TRACKING_EVENTS } from './tracking-constants.js';
import PopupManager from './popup-manager.js';
import SignPopup from './sign-popup.js';
import FormSubmission from './form-submission.js';
import ReserveModal from './reserve-modal.js';
import KlaviyoCapture from './klaviyo-capture.js';
import SearchToggle from './search-toggle.js';
import ProgressBar from './progress-bar.js';

if (typeof pe_mp_assets !== 'undefined' && pe_mp_assets.publicPath) {
    // Must run before any dynamic import() so webpack chunks resolve from /dist/.
    __webpack_public_path__ = pe_mp_assets.publicPath;
}

const trackEvent = TrackingCore.trackEvent.bind(TrackingCore);

window.trackEvent = trackEvent;
window.Tracking = Tracking;
window.TRACKING_EVENTS = TRACKING_EVENTS;
window.PopupManager = PopupManager;
window.FormSubmission = FormSubmission;

const SWIPER_SELECTOR = '.gallery-swiper, .gallery-thumbs, .providers-mosaic-swiper, .provider-conditions-swiper, .hero-conditions__carousel-wrapper';

function loadSwiperIfNeeded() {
    if (!document.querySelector(SWIPER_SELECTOR)) return;
    import(/* webpackChunkName: "swiper" */ './swiper.js').then((m) => {
        window.SwiperManager = m.default;
        setTimeout(() => m.default.initAllSwipers(), 300);
    });
}

function loadOptionalModules() {
    const jobs = [];

    if (document.querySelector('.faq-v2')) {
        jobs.push(import(/* webpackChunkName: "faq-v2" */ './faq-v2.js').then((m) => {
            window.FAQv2 = m.default;
            m.default.init();
        }));
    }

    if (document.querySelector('[data-promo-banner]')) {
        jobs.push(import(/* webpackChunkName: "promo" */ './promo-banner.js').then((m) => {
            m.default.init();
        }));
    }

    if (document.querySelector('.badge--verified, .badge--unclaimed, .badge--claimed, [data-verified-tooltip], [data-claimed-tooltip], [data-badge-tooltip-type="verified"], [data-badge-tooltip-type="claimed"], [data-badge-tooltip-type="unclaimed"]')) {
        jobs.push(import(/* webpackChunkName: "badge-tooltip" */ './verified-badge-tooltip.js').then((m) => {
            m.default.init();
        }));
    }

    if (document.querySelector('[data-search-results]')) {
        jobs.push(import(/* webpackChunkName: "search-more" */ './search-load-more.js').then((m) => {
            m.default.init();
        }));
    }

    if (document.querySelector('[data-tools-hub]')) {
        jobs.push(import(/* webpackChunkName: "tools-hub" */ './tools/tools-hub.js').then((m) => {
            m.initToolsHub();
        }));
    }

    if (document.querySelector('.home-fresh')) {
        jobs.push(import(/* webpackChunkName: "homepage-fresh" */ './homepage-fresh.js').then((m) => {
            m.initHomepageFreshCarousel();
        }));
    }

    if (document.querySelector('[data-apply-join-modal]')) {
        jobs.push(import(/* webpackChunkName: "apply-join-modal" */ './apply-join-modal.js').then((m) => {
            m.initApplyJoinModal();
        }));
    }

    if (document.querySelector('.home-new')) {
        jobs.push(import(/* webpackChunkName: "homepage-new" */ './homepage-new.js').then((m) => {
            m.initHomepageNew();
        }));
    }

    if (document.querySelector('.home-main')) {
        jobs.push(import(/* webpackChunkName: "homepage-main" */ './homepage-main.js').then((m) => {
            m.initHomepageMain();
        }));
    }

    if (document.querySelector('.membership-v2')) {
        // The billing toggle lives in this chunk; a failed fetch must not leave
        // the pricing buttons permanently inert with no sign anything is wrong.
        const loadMembershipV2 = () => import(/* webpackChunkName: "membership-v2" */ './membership-v2.js').then((m) => {
            m.initMembershipV2();
        });
        jobs.push(loadMembershipV2().catch(() => loadMembershipV2()).catch((err) => {
            console.error('[membership-v2] failed to load after retry; billing toggle is inert', err);
        }));
    }

    if (document.querySelector('[data-about-team], [data-about-v2-team]')) {
        jobs.push(import(/* webpackChunkName: "about" */ './about.js').then((m) => {
            m.initAboutPage();
            m.initAboutV2Team();
        }));
    }

    return Promise.all(jobs);
}

function loadAuthIfNeeded() {
    if (!document.querySelector('[data-signin-form]')) return;
    import(/* webpackChunkName: "auth" */ './auth.js').then((m) => {
        if (!window.SignIn) window.SignIn = m.default;
        m.default.init();
    });
}

const Menu = {
    initOffCanvasMenu() {
        const offCanvasToggleButtons = document.querySelectorAll('.toggle--off-canvas--menu');
        const offCanvas = document.querySelector('.off-canvas--menu');
        if (offCanvasToggleButtons.length === 0 || !offCanvas) return;

        const nav = offCanvas.querySelector('.off-canvas__navigation');
        const actions = offCanvas.querySelector('.off-canvas__actions');
        const navItems = [...offCanvas.querySelectorAll('.off-canvas__nav-item')];

        const updateActionsShadow = () => { if (actions && nav) actions.classList.toggle('off-canvas__actions--scrolled', nav.scrollTop > 0); };

        const switchToMenuMode = () => {
            document.body.classList.remove('off-canvas-opened-by-search');
            const firstWithSublist = navItems.find(item => item.querySelector('.off-canvas__nav-sublist'));
            if (firstWithSublist) firstWithSublist.classList.remove('is-collapsed');
        };

        const toggle = () => {
            const currentlyOpen = offCanvas.classList.contains('off-canvas--visible');
            if (currentlyOpen && document.body.classList.contains('off-canvas-opened-by-search')) { switchToMenuMode(); return; }
            if (!currentlyOpen) {
                if (nav) nav.scrollTop = 0;
                updateActionsShadow();
                const currentSection = nav?.getAttribute('data-current-section') ?? '';
                navItems.forEach(item => {
                    if (item.querySelector('.off-canvas__nav-sublist')) {
                        item.classList.add('is-collapsed');
                        if (currentSection && item.dataset.section === currentSection) item.classList.remove('is-collapsed');
                    }
                });
            }
            const isOpen = offCanvas.classList.toggle('off-canvas--visible');
            document.body.classList.toggle('off-canvas-menu-open', isOpen);
            if (!isOpen) {
                document.body.classList.remove('off-canvas-opened-by-search');
                offCanvas.querySelectorAll('.off-canvas__nav-other-toggle').forEach(btn => {
                    const targetId = btn.getAttribute('data-nav-other');
                    offCanvas.querySelectorAll(`[data-nav-other-target="${targetId}"]`).forEach(el => { el.hidden = true; });
                    btn.setAttribute('aria-expanded', 'false');
                    btn.classList.remove('is-expanded');
                });
                navItems.forEach(item => item.classList.remove('is-collapsed'));
            }
        };

        const runSectionToggle = (item, index, e, isTouch) => {
            const link = item.querySelector('.off-canvas__nav-link');
            if (link && (e.target === link || link.contains(e.target))) return;
            if (isTouch) e.preventDefault();
            const wasCollapsed = item.classList.contains('is-collapsed');
            if (wasCollapsed) { navItems.forEach(other => other.classList.add('is-collapsed')); item.classList.remove('is-collapsed'); }
            else item.classList.add('is-collapsed');
            if (nav && !item.classList.contains('is-collapsed')) {
                const stickyOffset = index * 48;
                if (nav.scrollTop > item.offsetTop - stickyOffset) nav.scrollTo({ top: Math.max(0, item.offsetTop - stickyOffset), behavior: 'smooth' });
            }
        };

        navItems.forEach((item, index) => {
            const row = item.querySelector('.off-canvas__nav-row');
            if (!row || !item.querySelector('.off-canvas__nav-sublist')) return;
            row.addEventListener('touchend', e => runSectionToggle(item, index, e, true), { passive: false });
            row.addEventListener('click', e => runSectionToggle(item, index, e, false));
        });

        if (nav) nav.addEventListener('scroll', updateActionsShadow);
        offCanvasToggleButtons.forEach(btn => btn.addEventListener('click', toggle));

        const expandMenuBtn = offCanvas.querySelector('[data-off-canvas-expand-menu]');
        if (expandMenuBtn) expandMenuBtn.addEventListener('click', switchToMenuMode);

        const closeMenu = () => {
            offCanvas.classList.remove('off-canvas--visible');
            document.body.classList.remove('off-canvas-menu-open', 'off-canvas-opened-by-search');
            offCanvas.querySelectorAll('.off-canvas__nav-other-toggle').forEach(btn => {
                const targetId = btn.getAttribute('data-nav-other');
                offCanvas.querySelectorAll(`[data-nav-other-target="${targetId}"]`).forEach(el => { el.hidden = true; });
                btn.setAttribute('aria-expanded', 'false');
                btn.classList.remove('is-expanded');
            });
            navItems.forEach(item => item.classList.remove('is-collapsed'));
        };

        const mobileSearchFocusBtn = offCanvas.querySelector('[data-mobile-search-focus]');
        const mobileSearchInput = offCanvas.querySelector('[data-mobile-search-input]');
        if (mobileSearchFocusBtn && mobileSearchInput) {
            mobileSearchFocusBtn.addEventListener('click', () => {
                document.body.classList.add('off-canvas-opened-by-search');
                navItems.forEach(item => item.classList.add('is-collapsed'));
                mobileSearchInput.focus();
            });
        }

        const closeBtn = offCanvas.querySelector('[data-off-canvas-close]');
        if (closeBtn) closeBtn.addEventListener('click', closeMenu);
        offCanvas.querySelectorAll('.off-canvas__nav-other-toggle').forEach(btn => {
            btn.addEventListener('click', () => {
                const targetId = btn.getAttribute('data-nav-other');
                const items = offCanvas.querySelectorAll(`[data-nav-other-target="${targetId}"]`);
                const expanded = items[0]?.hidden !== false;
                items.forEach(el => { el.hidden = !expanded; });
                btn.setAttribute('aria-expanded', expanded);
                btn.classList.toggle('is-expanded', expanded);
            });
        });
    }
};

const Accordion = {
    init() {
        document.querySelectorAll('.accordion__header').forEach(header => {
            header.addEventListener('click', (e) => { e.preventDefault(); this.toggleAccordion(header); });
            header.style.cursor = 'pointer';
        });
    },
    toggleAccordion(header) {
        const body = header.closest('.accordion')?.querySelector('.accordion__body');
        const arrow = header.querySelector('img');
        if (!body) return;
        const isOpen = body.classList.contains('accordion__body--open');
        body.classList.toggle('accordion__body--open', !isOpen);
        if (arrow) arrow.style.transform = isOpen ? 'rotate(0deg)' : 'rotate(180deg)';
    }
};

const FooterNavAccordion = {
    init() {
        const columns = document.querySelectorAll('.footer__nav-column--accordion');
        if (!columns.length) return;

        const desktopQuery = window.matchMedia('(min-width: 992px)');

        const syncColumns = () => {
            columns.forEach((column) => {
                const toggle = column.querySelector('.footer__nav-accordion-toggle');
                const panel = column.querySelector('.footer__nav-sublist');
                if (!toggle || !panel) return;

                if (desktopQuery.matches) {
                    panel.hidden = false;
                    toggle.setAttribute('aria-expanded', 'true');
                    return;
                }

                panel.hidden = true;
                toggle.setAttribute('aria-expanded', 'false');
            });
        };

        columns.forEach((column) => {
            const toggle = column.querySelector('.footer__nav-accordion-toggle');
            const panel = column.querySelector('.footer__nav-sublist');
            if (!toggle || !panel) return;

            toggle.addEventListener('click', () => {
                if (desktopQuery.matches) return;

                const expanded = toggle.getAttribute('aria-expanded') === 'true';
                toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
                panel.hidden = expanded;
            });
        });

        desktopQuery.addEventListener('change', syncColumns);
        syncColumns();
    },
};

const ArticleFaqAccordion = {
    init() {
        document.body.addEventListener('click', (e) => {
            const question = e.target.closest('.faq-item__question');
            if (!question) return;
            const item = question.closest('.faq-item');
            const list = question.closest('.faq-list');
            if (!item || !list) return;
            const isActive = item.classList.contains('--active');
            list.querySelectorAll('.faq-item').forEach((i) => i.classList.remove('--active'));
            if (!isActive) item.classList.add('--active');
        });
    }
};

/**
 * Homepage New / Membership FAQ — event delegation in main bundle
 * so accordion works even when lazy chunks fail to load.
 */
const PageFaqAccordion = {
    configs: [
        {
            list: '[data-home-new-faq]',
            item: '.home-new__faq-item',
            toggle: '[data-home-new-faq-toggle]',
            answer: '.home-new__faq-answer',
        },
        {
            list: '[data-membership-faq]',
            item: '.membership__faq-item',
            toggle: '[data-membership-faq-toggle]',
            answer: '.membership__faq-answer',
        },
        {
            list: '[data-home-main-faq]',
            item: '.home-main__faq-item',
            toggle: '[data-home-main-faq-toggle]',
            answer: '.home-main__faq-answer',
        },
        {
            list: '[data-membership-v2-faq]',
            item: '.membership-v2__faq-item',
            toggle: '[data-membership-v2-faq-toggle]',
            answer: '.membership-v2__faq-answer',
        },
    ],

    closestFromEvent(event, selector) {
        const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
        for (const node of path) {
            if (node && node.nodeType === 1 && typeof node.matches === 'function' && node.matches(selector)) {
                return node;
            }
        }
        const target = event.target;
        if (target && typeof target.closest === 'function') {
            return target.closest(selector);
        }
        return null;
    },

    init() {
        document.body.addEventListener('click', (e) => {
            for (const config of this.configs) {
                const toggle = this.closestFromEvent(e, config.toggle);
                if (!toggle) continue;

                const list = toggle.closest(config.list);
                const item = toggle.closest(config.item);
                if (!list || !item) continue;

                e.preventDefault();

                const isOpen = item.classList.contains('is-open');

                list.querySelectorAll(config.item).forEach((other) => {
                    const otherToggle = other.querySelector(config.toggle);
                    const otherAnswer = other.querySelector(config.answer);
                    other.classList.remove('is-open');
                    if (otherToggle) otherToggle.setAttribute('aria-expanded', 'false');
                    if (otherAnswer) otherAnswer.removeAttribute('hidden');
                });

                if (!isOpen) {
                    item.classList.add('is-open');
                    toggle.setAttribute('aria-expanded', 'true');
                }

                return;
            }
        });
    },
};

/**
 * Open Apply to join modal from main bundle so CTAs work even if the
 * lazy chunk fails to load (common report on mobile).
 */
const ApplyJoinModalLite = {
    init() {
        const modal = document.querySelector('[data-apply-join-modal]');
        if (!modal || modal.dataset.applyJoinLiteBound === 'true') {
            return;
        }
        modal.dataset.applyJoinLiteBound = 'true';

        const openModal = () => {
            modal.classList.add('is-open');
            document.body.style.overflow = 'hidden';
            const offCanvas = document.querySelector('.off-canvas--menu');
            if (offCanvas?.classList.contains('off-canvas--visible')) {
                offCanvas.classList.remove('off-canvas--visible');
                document.body.classList.remove('off-canvas-menu-open', 'off-canvas-opened-by-search');
            }
        };

        const closeModal = () => {
            modal.classList.remove('is-open');
            document.body.style.overflow = '';
        };

        document.body.addEventListener('click', (e) => {
            const openBtn = e.target.closest?.('[data-apply-join-open-modal]');
            if (openBtn) {
                if (openBtn.tagName === 'A') {
                    e.preventDefault();
                }
                openModal();
                return;
            }

            const closeBtn = e.target.closest?.('[data-apply-join-close-modal]');
            if (closeBtn && modal.contains(closeBtn)) {
                closeModal();
                return;
            }

            if (e.target === modal) {
                closeModal();
            }
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && modal.classList.contains('is-open')) {
                closeModal();
            }
        });

        if (window.location.hash === '#apply-to-join') {
            openModal();
        }
    },
};

const MembershipJoinModalLite = {
    init() {
        const modals = document.querySelectorAll('[data-membership-join-modal]');
        if (!modals.length || document.body.dataset.membershipJoinLiteBound === 'true') {
            return;
        }
        document.body.dataset.membershipJoinLiteBound = 'true';

        const openModal = (plan, options) => {
            const tryOpen = () => {
                if (typeof window.openMembershipJoinModal !== 'function') {
                    return false;
                }
                window.openMembershipJoinModal(plan, options || {});
                return true;
            };

            if (tryOpen()) {
                return;
            }

            const started = Date.now();
            const poll = () => {
                if (tryOpen() || Date.now() - started > 8000) {
                    return;
                }
                window.setTimeout(poll, 50);
            };
            poll();
        };

        const closeModals = () => {
            if (typeof window.closeMembershipJoinModals === 'function') {
                window.closeMembershipJoinModals();
                return;
            }
            document.querySelectorAll('.membership-join').forEach((modal) => {
                modal.classList.remove('is-open', 'is-checkout');
                modal.setAttribute('hidden', '');
            });
            if (!document.querySelector('[data-apply-join-modal].is-open')) {
                document.body.style.overflow = '';
            }
        };

        document.body.addEventListener('click', (e) => {
            const openBtn = e.target.closest?.('[data-membership-join-open]');
            if (openBtn) {
                e.preventDefault();
                openModal(openBtn.getAttribute('data-membership-join-open'));
                return;
            }

            const closeBtn = e.target.closest?.('[data-membership-join-close]');
            if (closeBtn) {
                closeModals();
                return;
            }

            if (e.target?.matches?.('[data-membership-join-modal]')) {
                closeModals();
            }
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && document.querySelector('.membership-join.is-open')) {
                closeModals();
            }
        });

        // Core and Core+ hashes belong to the reserve modal, which posts the lead before checkout.
        const hash = window.location.hash.replace('#', '');
        const stripeReturn = new URLSearchParams(window.location.search).get('membership_stripe') === '1';
        if ((hash === 'join-open' || hash === 'membership-open') && !stripeReturn) {
            openModal('open');
        }
    },
};

const JoinPsyStandardModal = {
    init() {
        const modal = document.querySelector('[data-join-psystandard-modal]');
        if (!modal || modal.dataset.joinBound === 'true') {
            return;
        }
        modal.dataset.joinBound = 'true';

        const formPanel = modal.querySelector('[data-join-psystandard-form-panel]');
        const successPanel = modal.querySelector('[data-join-psystandard-success]');
        const form = modal.querySelector('form');
        const joinCfg = window.pe_mp_join_form || {};
        let turnstileWidget = null;
        let turnstileLoader = null;
        let turnstilePending = false;
        let turnstileBypass = false;

        const ajaxUrl = () => joinCfg.ajax_url
            || (window.pe_mp_form_ajax && pe_mp_form_ajax.ajax_url)
            || '/wp-admin/admin-ajax.php';

        const mintTimeTrap = () => {
            if (!form) {
                return;
            }
            const body = new FormData();
            body.append('action', 'pe_mp_join_form_token');
            fetch(ajaxUrl(), {
                method: 'POST',
                body,
                credentials: 'same-origin',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            })
                .then((response) => response.json())
                .then((data) => {
                    if (!data || !data.success || !data.data) {
                        return;
                    }
                    const ts = form.querySelector('[name="form_ts"]');
                    const sig = form.querySelector('[name="form_sig"]');
                    if (ts) ts.value = data.data.form_ts || '';
                    if (sig) sig.value = data.data.form_sig || '';
                })
                .catch(() => {});
        };

        const loadTurnstile = () => {
            if (!joinCfg.turnstile_site_key) {
                return Promise.resolve();
            }
            if (window.turnstile) {
                return Promise.resolve();
            }
            if (turnstileLoader) {
                return turnstileLoader;
            }
            turnstileLoader = new Promise((resolve) => {
                const script = document.createElement('script');
                script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
                script.async = true;
                script.onload = () => resolve();
                script.onerror = () => resolve();
                document.head.appendChild(script);
            });
            return turnstileLoader;
        };

        const renderTurnstile = () => {
            if (!form || !joinCfg.turnstile_site_key || !window.turnstile) {
                return;
            }
            const slot = form.querySelector('[data-join-turnstile]');
            if (!slot) {
                return;
            }
            if (turnstileWidget !== null) {
                window.turnstile.reset(turnstileWidget);
                return;
            }
            turnstileWidget = window.turnstile.render(slot, {
                sitekey: joinCfg.turnstile_site_key,
                size: 'invisible',
                execution: 'render',
                callback: (token) => {
                    const waiting = turnstilePending;
                    turnstilePending = false;
                    let input = form.querySelector('[name="cf-turnstile-response"]');
                    if (!input) {
                        input = document.createElement('input');
                        input.type = 'hidden';
                        input.name = 'cf-turnstile-response';
                        form.appendChild(input);
                    }
                    if (token) {
                        input.value = token;
                    }
                    if (waiting && typeof form.requestSubmit === 'function') {
                        form.requestSubmit();
                    }
                },
                'error-callback': () => {
                    const waiting = turnstilePending;
                    turnstilePending = false;
                    if (!waiting) {
                        return;
                    }
                    turnstileBypass = true;
                    if (typeof form.requestSubmit === 'function') {
                        form.requestSubmit();
                    }
                },
                'expired-callback': () => {
                    const input = form.querySelector('[name="cf-turnstile-response"]');
                    if (input) {
                        input.value = '';
                    }
                },
            });
        };

        if (form) {
            form.addEventListener('submit', (event) => {
                if (!joinCfg.turnstile_site_key || turnstileWidget === null || !window.turnstile) {
                    return;
                }
                if (turnstileBypass) {
                    turnstileBypass = false;
                    return;
                }
                const token = form.querySelector('[name="cf-turnstile-response"]');
                if (token && token.value) {
                    return;
                }
                event.preventDefault();
                event.stopImmediatePropagation();
                if (turnstilePending) {
                    return;
                }
                turnstilePending = true;
                window.turnstile.execute(turnstileWidget);
            }, true);
        }

        const resetView = () => {
            modal.classList.remove('is-success');
            if (formPanel) {
                formPanel.hidden = false;
            }
            if (successPanel) {
                successPanel.hidden = true;
            }
            if (form) {
                form.reset();
            }
        };

        const openModal = () => {
            resetView();
            modal.hidden = false;
            modal.classList.add('is-open');
            document.body.style.overflow = 'hidden';
            const offCanvas = document.querySelector('.off-canvas--menu');
            if (offCanvas?.classList.contains('off-canvas--visible')) {
                offCanvas.classList.remove('off-canvas--visible');
                document.body.classList.remove('off-canvas-menu-open', 'off-canvas-opened-by-search');
            }
            const name = modal.querySelector('#join-ps-name');
            if (name) {
                window.setTimeout(() => name.focus(), 0);
            }
            mintTimeTrap();
            loadTurnstile().then(renderTurnstile);
        };

        const closeModal = () => {
            modal.classList.remove('is-open');
            modal.hidden = true;
            document.body.style.overflow = '';
            resetView();
        };

        window.showJoinPsyStandardSuccess = (target) => {
            const node = target || modal;
            const panel = node.querySelector('[data-join-psystandard-form-panel]');
            const success = node.querySelector('[data-join-psystandard-success]');
            node.classList.add('is-success');
            if (panel) {
                panel.hidden = true;
            }
            if (success) {
                success.hidden = false;
            }
        };

        document.body.addEventListener('click', (e) => {
            const openBtn = e.target.closest?.('[data-join-psystandard-open]');
            if (openBtn) {
                if (openBtn.tagName === 'A') {
                    e.preventDefault();
                }
                openModal();
                return;
            }

            const closeBtn = e.target.closest?.('[data-join-psystandard-close]');
            if (closeBtn && modal.contains(closeBtn)) {
                closeModal();
            }
        });

        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                closeModal();
            }
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && modal.classList.contains('is-open')) {
                closeModal();
            }
        });

        if (window.location.hash === '#join-psystandard') {
            openModal();
        }
    },
};

const TagChipsToggle = {
    init() {
        document.querySelectorAll('.tag-chips__toggle, .provider-single__tags-toggle').forEach(button => {
            button.addEventListener('click', (e) => { e.preventDefault(); this.toggle(button); });
        });
    },
    toggle(button) {
        const group = button.closest('.tag-chips__group, .provider-single__tags-group');
        if (!group) return;
        const isExpanded = group.getAttribute('data-expanded') === 'true';
        if (isExpanded) { group.removeAttribute('data-expanded'); button.setAttribute('aria-expanded', 'false'); }
        else { group.setAttribute('data-expanded', 'true'); button.setAttribute('aria-expanded', 'true'); }
    }
};

const Tabs = {
    init() {
        document.querySelectorAll('.tabs').forEach(container => {
            const tabButtons = container.querySelectorAll('.tabs__button');
            const tabContents = container.querySelectorAll('.tabs__content[data-tab]');
            tabButtons.forEach(button => {
                button.addEventListener('click', () => {
                    const targetTab = button.getAttribute('data-tab');
                    tabButtons.forEach(btn => btn.classList.remove('tabs__button--active'));
                    tabContents.forEach(content => content.classList.remove('tabs__content--active'));
                    button.classList.add('tabs__button--active');
                    container.querySelector(`.tabs__content[data-tab="${targetTab}"]`)?.classList.add('tabs__content--active');
                });
            });
            if (tabButtons.length > 0) tabButtons[0].click();
        });
    }
};

const HeaderScrollToggle = {
    init() {
        const header = document.querySelector('.header');
        if (!header) return;
        const mobileQuery = window.matchMedia('(max-width: 767px)');
        const tocMobile = document.querySelector('[data-toc-mobile]');
        const headerTocToggle = document.querySelector('[data-header-toc-toggle]');
        let lastY = window.scrollY, ticking = false, scrollListener = null;

        const isTocOpen = () => tocMobile && tocMobile.classList.contains('is-open');
        const closeTocDropdown = () => { if (tocMobile) tocMobile.classList.remove('is-open'); if (headerTocToggle) headerTocToggle.setAttribute('aria-expanded', 'false'); };

        const update = () => {
            ticking = false;
            const currentY = window.scrollY;
            if (mobileQuery.matches && headerTocToggle) {
                const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
                headerTocToggle.style.setProperty('--toc-scroll-fill', String(maxScroll > 0 ? Math.min(100, (currentY / maxScroll) * 100) : 0));
            }
            if (currentY <= 10) { header.classList.remove('header--hidden'); lastY = currentY; return; }
            if (isTocOpen()) { lastY = currentY; return; }
            if (currentY > lastY + 4 && currentY > 50) { header.classList.add('header--hidden'); closeTocDropdown(); }
            else if (currentY < lastY - 4) header.classList.remove('header--hidden');
            lastY = currentY;
        };

        const onScroll = () => { if (!ticking) { window.requestAnimationFrame(update); ticking = true; } };
        const setupScrollListener = () => {
            if (mobileQuery.matches) { if (!scrollListener) { scrollListener = onScroll; window.addEventListener('scroll', scrollListener, { passive: true }); } update(); }
            else { header.classList.remove('header--hidden'); if (headerTocToggle) headerTocToggle.style.removeProperty('--toc-scroll-fill'); if (scrollListener) { window.removeEventListener('scroll', scrollListener); scrollListener = null; } }
        };
        setupScrollListener();
        mobileQuery.addEventListener('change', setupScrollListener);
    }
};

const InputLabels = {
    init() {
        document.querySelectorAll('.input-v2').forEach(input => {
            const placeholder = input.getAttribute('placeholder');
            if (placeholder && !input.parentNode.querySelector('.input-v2__label')) {
                const label = document.createElement('span');
                label.className = 'input-v2__label';
                label.textContent = placeholder;
                input.parentNode.insertBefore(label, input.nextSibling);
            }
        });
    }
};

window.InputLabels = InputLabels;
window.SignPopup = SignPopup;

function initCoreUi() {
    SignPopup.init();
    PopupManager.init();
    loadSwiperIfNeeded();
    loadOptionalModules();
    loadAuthIfNeeded();

    InputLabels.init();
    Menu.initOffCanvasMenu();
    SearchToggle.init();
    Accordion.init();
    FooterNavAccordion.init();
    Tabs.init();
    TagChipsToggle.init();
    ProgressBar.init();
    FormSubmission.init();
    KlaviyoCapture.init();
    ArticleFaqAccordion.init();
    PageFaqAccordion.init();
    ApplyJoinModalLite.init();
    MembershipJoinModalLite.init();
    JoinPsyStandardModal.init();
    ReserveModal.init();
    HeaderScrollToggle.init();
    Tracking.init();

    if (document.querySelector('[data-account-form], #delete-account-btn, #delete-test-results-btn')) {
        import(/* webpackChunkName: "account" */ './account.js').then(m => m.initAccount());
    }

    if (document.querySelector('.article-v2, .single-post, [data-toc]')) {
        import(/* webpackChunkName: "article" */ './toc.js').then(m => m.default.init());
        import(/* webpackChunkName: "article" */ './share.js').then(m => m.default.init());
        import(/* webpackChunkName: "article" */ './reactions.js').then(m => m.default.init());
        import(/* webpackChunkName: "article" */ './expert-reviewed-link.js').then(m => m.default.init());
        import(/* webpackChunkName: "article" */ './sticky-cta.js').then(m => m.default.init());
    }

    if (document.querySelector('.providers-toolbar, .provider-filter, .provider-search, [data-provider-filter]')) {
        setTimeout(() => {
            import(/* webpackChunkName: "providers" */ './providers-toolbar.js').then(m => m.default.init());
            import(/* webpackChunkName: "providers" */ './provider-filter.js').then(m => m.default.init());
            import(/* webpackChunkName: "providers" */ './provider-navigator.js').then(m => m.default.init());
            import(/* webpackChunkName: "providers" */ './provider-search.js').then(m => m.default.init());
        }, 500);
    }
    if (document.querySelector('.provider-single__card-more-contacts, .provider-single__cover-logo')) {
        import(/* webpackChunkName: "providers" */ './provider-extras.js').then(m => m.initProviderExtras());
    }

    if (document.querySelector('#test-options-form, .test-landing__link')) {
        import(/* webpackChunkName: "test" */ './test-page.js').then(m => m.initTestPage());
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const gatedReady = document.querySelector('.gated-file-block')
        ? import(/* webpackChunkName: "gated-file" */ './gated-file-popup.js').then((m) => {
            window.GatedFilePopup = m.default;
            m.default.init();
        })
        : Promise.resolve();

    gatedReady.then(() => initCoreUi());
});
